/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values */
/* eslint-disable @atlaskit/ui-styling-standard/no-unsafe-values */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useRef, useState } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { getDocument } from '@atlaskit/browser-apis';

import { SectionName } from '../../../common/analytics/section-name';
import { useSectionReveal } from '../../../common/analytics/useSectionReveal';
import { NAV_BAR_HEIGHT_PX } from '../NavBar';

import { BackgroundIcons } from './BackgroundIcons';
import { ContentStack } from './ContentStack';
import { usePointerInteractivity } from './use-pointer-interactivity';
import { usePrefersReducedMotion } from './use-prefers-reduced-motion';
import { useViewportTier } from './use-viewport-tier';

const EDGE_CONFIG = {
	density: 3,
	radius: 600,
	opacity: 0.25,
} as const;

const LAYOUT_CONFIG = {
	ovalSize: 0.82,
	ovalAspectRatio: 1.19,
	mobileOvalSize: 1.16,
	mobileOvalAspectRatio: 0.85,
} as const;

const AMBIENT_CONFIG = {
	ambientAmp: 2.5,
} as const;

const PHYSICS_CONFIG = {
	kSelf: 80,
	kEdge: 20,
	damping: 8,
	flick: 0.5,
} as const;

const INTRO_CONFIG = {
	avatarStaggerMs: 60,
	edgeGrowMs: 800,
	iconAppearMs: 300,
	iconAppearOvershoot: 1.8,
} as const;

const SHIMMER_CONFIG = {
	enabled: true,
	startOffsetMs: -2000,
	avgIntervalMs: 120,
	maxConcurrent: 10,
	durationMs: 1420,
	pulseLen: 0.37,
	brightness: 0.5,
	strokeWidth: 1.5,
	glow: 0.3,
} as const;

const TYPOGRAPHY_HERO_CONFIG = {
	scale: 1,
} as const;

const TYPOGRAPHY_LETTER_A_CONFIG = {
	lineWidth: 3,
	durationMs: 800,
	startTimeMs: -1970,
	nodePhaseStart: 0.1,
	nodePhaseEnd: 1,
	shuffleNodes: false,
	shuffleSeed: 300,
} as const;

const TYPOGRAPHY_SCRIBBLE_CONFIG = {
	durationMs: 1000,
	startTimeMs: 700,
	ease: 'ease-out',
	lineWidth: 10.5,
	blurPx: 0.5,
} as const;

const TYPOGRAPHY_LETTER_INTRO_CONFIG = {
	style: 'fade-in',
	fadeDurationMs: 400,
	blurStartPx: 8,
	totalDurationMs: 450,
	startDelayMs: 480,
	shuffleSeed: 532,
} as const;

const CTA_TIMING_CONFIG = {
	subtitleStartMs: 1000,
	subtitleFadeMs: 400,
	ctaStartMs: 1150,
	ctaFadeMs: 500,
} as const;

const TYPOGRAPHY_MOUNT_KEY = 'twg-hero-defaults';

function initialIntroDone(): boolean {
	const doc = getDocument();
	if (!doc) return false;
	return doc.visibilityState !== 'visible';
}

const styles = cssMap({
	stageWrapper: {
		position: 'relative',
		width: '100%',
		height: '1000px',
		overflow: 'hidden',
		backgroundColor: '#111213',
	},
	artboard: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		width: '1680px',
		height: '1200px',
		transform: 'translate(-50%, -50%)',
	},
	mobileStageWrapper: {
		height: `calc(100dvh - ${NAV_BAR_HEIGHT_PX}px)`,
	},
	mobileArtboard: {
		width: '650px',
		height: `calc(100dvh - ${NAV_BAR_HEIGHT_PX}px)`,
	},
});

export function HeroIcons(): JSX.Element {
	const stageRef = useRef<HTMLDivElement | null>(null);
	const sectionRef = useSectionReveal<HTMLDivElement>(SectionName.Hero);
	const viewportTier = useViewportTier();
	const iconsInteractive = usePointerInteractivity();
	const reducedMotion = usePrefersReducedMotion();
	const [introDone, setIntroDone] = useState<boolean>(initialIntroDone);
	const replayHandleRef = useRef<(() => void) | null>(null);
	const isMobileLayout = viewportTier === 'mobile';

	const onIntroComplete = useCallback(() => {
		setIntroDone(true);
	}, []);

	const effectiveAmbient = reducedMotion ? { ...AMBIENT_CONFIG, ambientAmp: 0 } : AMBIENT_CONFIG;
	const effectiveIntro = reducedMotion
		? {
			...INTRO_CONFIG,
			avatarStaggerMs: 0,
			edgeGrowMs: 0,
			iconAppearMs: 0,
			iconAppearOvershoot: 1,
		}
		: INTRO_CONFIG;
	const effectiveShimmer = reducedMotion
		? { ...SHIMMER_CONFIG, enabled: false, maxConcurrent: 1, glow: 0 }
		: SHIMMER_CONFIG;

	return (
		<div ref={sectionRef} css={[styles.stageWrapper, isMobileLayout && styles.mobileStageWrapper]}>
			<div ref={stageRef} css={[styles.artboard, isMobileLayout && styles.mobileArtboard]}>
				<BackgroundIcons
					stageRef={stageRef}
					introDone={introDone}
					onIntroComplete={onIntroComplete}
					replayHandleRef={replayHandleRef}
					edges={EDGE_CONFIG}
					layout={LAYOUT_CONFIG}
					ambient={effectiveAmbient}
					physics={PHYSICS_CONFIG}
					intro={effectiveIntro}
					shimmer={effectiveShimmer}
					viewportTier={viewportTier}
					interactive={iconsInteractive}
				/>
				<ContentStack
					mountKey={TYPOGRAPHY_MOUNT_KEY}
					viewportTier={viewportTier}
					reducedMotion={reducedMotion}
					hero={TYPOGRAPHY_HERO_CONFIG}
					letterA={TYPOGRAPHY_LETTER_A_CONFIG}
					scribble={TYPOGRAPHY_SCRIBBLE_CONFIG}
					letterIntro={TYPOGRAPHY_LETTER_INTRO_CONFIG}
					cta={CTA_TIMING_CONFIG}
				/>
			</div>
		</div>
	);
}
